import React, { useEffect, useState } from "react";
import { useNotification } from "../../NotificationContext";
import { Select } from "antd";
import {
  getPatientTransaction,
  savePatientTransaction,
  updatePatientTransaction,
} from "../../apis/patientTransaction";
import { getPatients } from "../../apis/patient";
import DatePicker from "react-datepicker";
import { format } from "date-fns";

const AddPatientTransaction: React.FC<{
  closeModal: () => void;
  editableId: any;
  preSelectedPatientId?: number;
}> = ({ closeModal, editableId, preSelectedPatientId }) => {
  const defaultTransaction = {
    patientId: preSelectedPatientId || null,
    amount: "",
    paymentMode: "",
    note: "",
    date: format(new Date(), "yyyy-MM-dd HH:mm").toString(),
  };
  const [transaction, setTransaction] = useState(defaultTransaction);
  const [errors, setErrors] = useState<any>();
  const { showNotification } = useNotification();
  const [patients, setPatients] = useState<any[]>([]);

  const paymentModes = [
    {
      name: "Cash",
      value: "cash",
    },
    {
      name: "Cheque",
      value: "cheque",
    },
    {
      name: "Online-Transfer",
      value: "online-transfer",
    },
    {
      name: "UPI",
      value: "upi",
    },
  ];

  useEffect(() => {
    fetchPatients();
    if (editableId) {
      fetchTransaction(editableId);
    }
  }, [editableId]);

  const fetchPatients = () => {
    getPatients({ per_page: 1000 }).then((res: any) => {
      setPatients(res.data.data || res.data || []);
    });
  };

  const fetchTransaction = (id: number) => {
    getPatientTransaction(id).then((res: any) => {
      const data = res.data.data || res.data;
      setTransaction({
        patientId: data.patientId || data.patient_id || null,
        amount: data.amount || "",
        paymentMode: data.paymentMode || data.payment_mode || "",
        note: data.note || "",
        date: data.date
          ? format(new Date(data.date), "yyyy-MM-dd HH:mm").toString()
          : format(new Date(), "yyyy-MM-dd HH:mm").toString(),
      });
    });
  };

  const validateForm = () => {
    const validationErrors: any = {};

    if (!transaction?.patientId) {
      validationErrors.patientId = "* Patient is required";
    }
    if (!transaction?.amount || parseFloat(transaction?.amount) <= 0) {
      validationErrors.amount = "* Amount should be greater than 0";
    }
    if (transaction?.paymentMode === "") {
      validationErrors.paymentMode = "* Payment mode is required";
    }

    const hasErrors = Boolean(Object.keys(validationErrors).length);

    return { hasErrors, validationErrors };
  };

  const addTransaction = () => {
    const { hasErrors, validationErrors } = validateForm();
    if (hasErrors) {
      setErrors(validationErrors);
      return;
    }
    setErrors(null);

    savePatientTransaction({
      ...transaction,
    })
      .then((res: any) => {
        showNotification(
          "success",
          "Patient Transaction Added successfully",
          "",
          "topRight"
        );
        closeModal();
      })
      .catch((err) => {
        showNotification(
          "error",
          "Failed to add patient transaction",
          "",
          "topRight"
        );
      });
  };

  const updateTransactionData = () => {
    const { hasErrors, validationErrors } = validateForm();
    if (hasErrors) {
      setErrors(validationErrors);
      return;
    }
    setErrors(null);
    updatePatientTransaction({
      ...transaction,
      id: editableId,
    })
      .then((res) => {
        showNotification(
          "success",
          "Patient Transaction Updated successfully",
          "",
          "topRight"
        );
        closeModal();
      })
      .catch((err) => {
        showNotification(
          "error",
          "Failed to update patient transaction",
          "",
          "topRight"
        );
      });
  };

  const onSave = () => {
    if (editableId) {
      updateTransactionData();
    } else {
      addTransaction();
    }
  };

  const setPaymentMode = (paymentMode: string) => {
    setTransaction({
      ...transaction,
      paymentMode,
    });
  };

  const handleDateChange = (date: any) => {
    setTransaction({
      ...transaction,
      date: format(date, "yyyy-MM-dd HH:mm").toString(),
    });
  };

  const handlePatientChange = (patientId: any) => {
    setTransaction({
      ...transaction,
      patientId,
    });
    setErrors({ ...errors, patientId: undefined });
  };

  return (
    <div className="">
      {!preSelectedPatientId && (
        <div className="pt-2">
          <label className="block text-sm font-medium leading-6 text-gray-900">
            Patient <span className="text-red-500">*</span>
          </label>
          <div className="mt-1">
            <Select
              style={{ width: "100%" }}
              onChange={handlePatientChange}
              value={transaction.patientId}
              placeholder="Select Patient"
              showSearch
              filterOption={(input, option: any) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={patients.map((patient) => ({
                value: patient.id,
                label: `${patient.firstName || ""} ${
                  patient.lastName || ""
                }`.trim(),
              }))}
            />
          </div>
          {errors && errors.patientId && (
            <span className="text-sm text-red-500 leading-0">
              {errors.patientId}
            </span>
          )}
        </div>
      )}
      <div className="block mb-3 pt-2">
        <span className="text-sm font-medium text-gray-700">Date</span>
        <DatePicker
          selected={new Date(transaction.date)}
          onChange={handleDateChange}
          portalId="root-panel"
          showTimeSelect={true}
          timeFormat="h:mm aa"
          dateFormat="MMMM d, yyyy h:mm aa"
          className="block w-full mt-1 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
        />
        {errors && errors.date && (
          <span className="text-sm text-red-500 leading-0">{errors.date}</span>
        )}
      </div>
      <div className="pt-2">
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Amount <span className="text-red-500">*</span>
        </label>
        <div className="mt-1">
          <input
            id="amount"
            name="amount"
            type="number"
            min="0"
            step="0.01"
            value={transaction?.amount}
            onChange={(e) =>
              setTransaction({ ...transaction, amount: e.target.value })
            }
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          />
        </div>
        {errors && errors.amount && (
          <span className="text-sm text-red-500 leading-0">
            {errors.amount}
          </span>
        )}
      </div>
      <div className="pt-2">
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Payment Mode <span className="text-red-500">*</span>
        </label>
        <div className="mt-1">
          <Select
            style={{ width: "100%" }}
            onChange={setPaymentMode}
            value={transaction.paymentMode}
            placeholder="Select Payment Mode"
            options={paymentModes}
          />
        </div>
        {errors && errors.paymentMode && (
          <span className="text-sm text-red-500 leading-0">
            {errors.paymentMode}
          </span>
        )}
      </div>
      <div className="pt-2">
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Payment Note
        </label>
        <div className="mt-1">
          <input
            id="paymentNote"
            name="paymentNote"
            type="text"
            value={transaction?.note}
            onChange={(e) =>
              setTransaction({ ...transaction, note: e.target.value })
            }
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          />
        </div>
        {errors && errors.paymentNote && (
          <span className="text-sm text-red-500 leading-0">
            {errors.paymentNote}
          </span>
        )}
      </div>
      <div className="flex gap-x-6 justify-start items-center py-4 border-gray-900/10">
        <button
          onClick={() => onSave()}
          type="submit"
          className="px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dark"
        >
          {editableId ? "Update" : "Save"}
        </button>
        <button
          onClick={() => closeModal()}
          type="button"
          className="text-sm font-semibold leading-6 text-gray-900"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default AddPatientTransaction;
